import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight, X, Loader2, FileWarning } from "lucide-react"
import type * as Y from "yjs"
import { Button } from "@/components/ui/button"
import { useClassroomDoc } from "./LiveDocProvider"
import { CollabCanvas } from "./CollabCanvas"

export interface SharedDoc {
  url: string
  title: string
  page: number
}

// Pure read of the shared-document state from its Yjs map (no shared doc -> null).
export function readSharedDoc(map: Y.Map<unknown>): SharedDoc | null {
  const url = map.get("url")
  if (typeof url !== "string" || url.length === 0) return null
  return {
    url,
    title: (map.get("title") as string) ?? "Document",
    page: (map.get("page") as number) ?? 1,
  }
}

export function clampPage(page: number, total: number): number {
  if (total <= 0) return 1
  if (page < 1) return 1
  if (page > total) return total
  return page
}

// Reads/writes the shared "document" state in the session's Yjs doc — the
// currently-shared PDF (url + title) and the page both participants are on.
// Setting/clearing it propagates to the other peer, so opening a material or
// flipping a page is mirrored on both screens.
export function useSharedDocument() {
  const { doc } = useClassroomDoc()
  const mapRef = useRef(doc.getMap("document"))

  const read = useCallback((): SharedDoc | null => readSharedDoc(mapRef.current), [])

  const [document, setDocument] = useState<SharedDoc | null>(() => read())

  useEffect(() => {
    const map = mapRef.current
    const observer = () => setDocument(read())
    map.observe(observer)
    return () => map.unobserve(observer)
  }, [read])

  const open = useCallback(
    (material: { documentUrl: string | null; title: string }) => {
      if (!material.documentUrl) return
      const map = mapRef.current
      doc.transact(() => {
        map.set("url", material.documentUrl)
        map.set("title", material.title)
        map.set("page", 1)
      })
    },
    [doc],
  )

  const setPage = useCallback((page: number) => {
    mapRef.current.set("page", page)
  }, [])

  const close = useCallback(() => {
    const map = mapRef.current
    doc.transact(() => {
      map.delete("url")
      map.delete("title")
      map.delete("page")
    })
  }, [doc])

  return { document, active: document !== null, open, setPage, close }
}

// Renders the shared PDF page (pdf.js) on the classroom board with synced page
// navigation and a co-annotation layer on top. Driven entirely by the shared
// Yjs state, so both participants see the same page and each other's drawing.
export function SharedDocument() {
  const { t } = useTranslation()
  const { document: shared, setPage, close } = useSharedDocument()
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfRef = useRef<any>(null)
  const loadTaskRef = useRef<any>(null)
  const renderTokenRef = useRef(0)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTaskRef = useRef<any>(null)
  const [numPages, setNumPages] = useState(0)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loading, setLoading] = useState(true)

  const url = shared?.url
  const page = shared?.page ?? 1
  // Mirror `page` into a ref so the async PDF-load effect renders the LATEST
  // synced page even if a peer flipped pages while the PDF was still loading —
  // otherwise it renders the stale page captured when the load started.
  const pageRef = useRef(page)
  pageRef.current = page

  const renderPage = useCallback(async (pageNum: number) => {
    const pdf = pdfRef.current
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!pdf || !canvas || !container) return

    const token = ++renderTokenRef.current
    // Cancel any render still in flight — pdf.js throws if two render() calls
    // touch the same canvas concurrently (a resize firing mid-render, a synced
    // page flip, etc.).
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel()
      } catch {
        /* already settled */
      }
      renderTaskRef.current = null
    }

    let pageObj
    try {
      pageObj = await pdf.getPage(clampPage(pageNum, pdf.numPages))
    } catch {
      return
    }
    if (token !== renderTokenRef.current) return // superseded by a newer render

    const dpr = window.devicePixelRatio || 1
    const base = pageObj.getViewport({ scale: 1 })
    const fit = Math.min(container.clientWidth / base.width, container.clientHeight / base.height)
    const viewport = pageObj.getViewport({ scale: Math.max(fit, 0.1) * dpr })

    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    canvas.style.width = `${viewport.width / dpr}px`
    canvas.style.height = `${viewport.height / dpr}px`
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const task = pageObj.render({ canvasContext: ctx, viewport, canvas })
    renderTaskRef.current = task
    try {
      await task.promise
    } catch {
      // RenderingCancelledException (or a destroyed doc) — a newer render owns the canvas.
      return
    }
    if (renderTaskRef.current === task) renderTaskRef.current = null
  }, [])

  // Load the PDF when the shared url changes.
  useEffect(() => {
    if (!url) return
    let cancelled = false
    setLoading(true)
    setLoadFailed(false)
    setNumPages(0) // hide page-nav until the NEW pdf reports its count (no stale "5 / 3")
    ;(async () => {
      try {
        // Legacy build: pdf.js 6's standard build targets very recent browsers;
        // legacy keeps the documented floor (Chrome 125 / Safari 18) for
        // students on older devices.
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs")
        // @ts-ignore — Vite resolves the worker URL at build time (no decl for the ?url import).
        const worker = (await import("pdfjs-dist/legacy/build/pdf.worker.mjs?url")).default
        pdfjs.GlobalWorkerOptions.workerSrc = worker
        const task = pdfjs.getDocument({ url })
        loadTaskRef.current = task
        const pdf = await task.promise
        if (cancelled) return
        pdfRef.current = pdf
        setNumPages(pdf.numPages)
        setLoading(false)
        await renderPage(pageRef.current)
      } catch {
        if (!cancelled) {
          setLoadFailed(true)
          setLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
      renderTokenRef.current++ // abort any in-flight render against the old pdf
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel()
        } catch {
          /* settled */
        }
        renderTaskRef.current = null
      }
      // Destroy via the loading task: pdf.js 6 removed PDFDocumentProxy#destroy,
      // and the task also covers a load still in flight when the url swaps.
      loadTaskRef.current?.destroy?.().catch?.(() => {})
      loadTaskRef.current = null
      pdfRef.current = null
    }
    // Reload only on url change; page re-renders are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  // Re-render on page change.
  useEffect(() => {
    if (pdfRef.current) renderPage(page)
  }, [page, renderPage])

  // Re-render on container resize.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(() => {
      if (pdfRef.current) renderPage(page)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [page, renderPage])

  if (!shared) return null

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-background">
      <div className="flex items-center gap-2 px-3 h-10 border-b border-border bg-card shrink-0">
        <span className="text-sm font-medium truncate flex-1">{shared.title}</span>
        {numPages > 0 && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(clampPage(page - 1, numPages))} aria-label={t("liveSession.sharedDocument.previousPage")}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">
              {page} / {numPages}
            </span>
            <Button variant="ghost" size="sm" disabled={page >= numPages} onClick={() => setPage(clampPage(page + 1, numPages))} aria-label={t("liveSession.sharedDocument.nextPage")}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={close} aria-label={t("liveSession.sharedDocument.close")}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div ref={containerRef} className="flex-1 relative min-h-0 flex items-center justify-center overflow-hidden bg-muted/30">
        {loadFailed ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <FileWarning className="w-8 h-8" />
            <p className="text-sm">{t("liveSession.sharedDocument.loadError")}</p>
          </div>
        ) : (
          <>
            <canvas ref={canvasRef} className="shadow-card bg-white" />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
            {/* Co-annotation layer over the document (its own stroke surface). */}
            <div className="absolute inset-0">
              <CollabCanvas surface="document" transparent />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
